import { Component, EventEmitter, HostListener, inject, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationProfile } from '@app/@shared/models/application-profile.model';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MenuItem } from 'primeng/api';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/angular-fontawesome';
import { isNotNullOrUndefined } from '@ep/shared';
import { Store } from '@ngrx/store';
import { AuthenticationActions } from '@app/resources/ngrx/actions';
import { BreadcrumbService } from '@app/resources/services/breadcrumb.service';
import { FONT_AWESOME_ICONS } from '@shared/icons';
import { CredentialsService } from '@app/auth';
import { MerchantNameVisibilityService } from '@shared/services/merchant-name-visibility.service';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {
  @Output() closeClicked = new EventEmitter<void>();

  private readonly store = inject(Store);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly credentialsService = inject(CredentialsService);
  private readonly merchantNameVisibilityService = inject(MerchantNameVisibilityService);

  // Icons
  faBars = faBars;
  icons = FONT_AWESOME_ICONS;

  items!: MenuItem[];
  applicationProfile!: ApplicationProfile;
  fullUserName!: string;
  impersonatedMerchantName?: string;
  isMerchantNameVisible: boolean = true;
  isMobileScreen: boolean = false;

  constructor(
    private router: Router,
    private applicationProfileService: ApplicationProfileService,
    private cssValidatorService: CssValidatorService
  ) {}

  get breadcrumbs$() {
    return this.breadcrumbService.breadcrumbs$;
  }

  get shouldShowBreadcrumbs() {
    return this.breadcrumbService.shouldShowBreadcrumbs;
  }

  // Breadcrumb icons name whatever the matching side navigation entry uses: a font awesome icon by
  // key, or the css classes of an icon font.
  protected faIcon(icon?: string): IconDefinition | null {
    return isNotNullOrUndefined(icon) ? this.icons[icon] ?? null : null;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.isMobileScreen = window.innerWidth <= 550;
  }

  ngOnInit() {
    this.onResize({});

    this.merchantNameVisibilityService.isVisible$.pipe(untilDestroyed(this)).subscribe((isVisible) => {
      this.isMerchantNameVisible = isVisible;
      this.buildMenuItems();
    });

    this.applicationProfileService.applicationProfile$
      .pipe(untilDestroyed(this))
      .subscribe((applicationProfile: ApplicationProfile) => {
        this.applicationProfile = applicationProfile;
        this.fullUserName = `${applicationProfile.Employee.NameFirst} ${applicationProfile.Employee.NameLast}`;
        this.impersonatedMerchantName = this.credentialsService.isImpersonation()
          ? applicationProfile.Merchant?.Name
          : undefined;
        this.setCSSCustomProperties();
      });
  }

  onToggleMerchantInfoClicked(): void {
    if (this.isMerchantNameVisible) {
      this.merchantNameVisibilityService.hide();
    } else {
      this.merchantNameVisibilityService.show();
    }
  }

  private buildMenuItems(): void {
    this.items = [
      ...(this.credentialsService.isImpersonation()
        ? [
            {
              label: 'Hide/Show Merchant Info',
              icon: this.isMerchantNameVisible ? 'pi pi-eye-slash' : 'pi pi-eye',
              command: () => {
                this.onToggleMerchantInfoClicked();
              },
            },
          ]
        : []),
      {
        label: 'Change Password',
        icon: 'pi pi-user-edit',
        command: () => {
          this.onChangePasswordClicked();
        },
      },
      {
        label: 'Log Out',
        icon: 'pi pi-sign-out',
        command: () => {
          this.onLogoutClicked();
        },
      },
    ];
  }

  onLogoutClicked(): void {
    this.store.dispatch(AuthenticationActions.authenticationLogout());
  }

  onChangePasswordClicked(): void {
    this.router.navigate([`${this.applicationProfile.Location.LocationId}/change-password`]);
  }

  onCloseClicked(event?: any): void {
    this.closeClicked.emit(event);
  }

  private setCSSCustomProperties(): void {
    if (this.applicationProfile?.PortalTheme?.Theme) {
      const theme = this.applicationProfile.PortalTheme.Theme;
      // Set CSS custom properties
      document.documentElement.style.setProperty('--header-bg-primary', theme.header?.backgroundColor);
      document.documentElement.style.setProperty('--header-primary', theme.header?.primary);
      document.documentElement.style.setProperty('--main-content-bg', theme.backgroundColor);
      document.documentElement.style.setProperty('--sidebar-bg-primary', theme.sidebar?.backgroundColor?.primary);
      document.documentElement.style.setProperty('--sidebar-bg-secondary', theme.sidebar?.backgroundColor?.secondary);
      document.documentElement.style.setProperty('--sidebar-text-color', theme.sidebar?.textColor);
      document.documentElement.style.setProperty('--primary', theme.primary);
      document.documentElement.style.setProperty('--header-items-color', theme.header?.itemsColor);
      document.documentElement.style.setProperty('--header-text-color', theme.header?.textColor);

      // Check for undefined CSS variables
      this.cssValidatorService.checkUndefinedCssVariables('HeaderComponent');
    }
  }
}
